<div
  *ngIf="isOpen"
  class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
  (click)="$event.target === $event.currentTarget && !isCallInProgress && !callCompleted && close()"
  role="dialog"
  aria-modal="true"
>
  <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center relative">

    <!-- Close X button — hidden while actively ringing/connected -->
    <button
      *ngIf="!isCallInProgress"
      type="button"
      (click)="close()"
      class="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition p-1 rounded-md hover:bg-gray-100"
      title="Close"
    >
      <app-icon name="x" [size]="16"></app-icon>
    </button>

    <!-- ─── COMPLETED STATE ───────────────────────────────────────────── -->
    <ng-container *ngIf="callCompleted && !isCallInProgress">
      <!-- Green checkmark circle -->
      <div class="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
        <app-icon name="check-circle" [size]="32" class="text-emerald-600"></app-icon>
      </div>

      <h3 class="text-base font-semibold text-gray-900 mb-0.5">Call Completed</h3>
      <p class="text-gray-500 font-mono text-xs mb-2">{{ resolvedPatientName }}</p>

      <!-- Duration pill -->
      <div class="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 rounded-full mb-4">
        <app-icon name="clock" [size]="12" class="text-emerald-600"></app-icon>
        <span class="text-emerald-700 font-mono font-semibold text-xs">{{ formatCallTimer() }}</span>
      </div>

      <!-- Call Remarks / Notes for Timeline -->
      <div class="mb-4 text-left">
        <div class="flex items-center justify-between mb-1">
          <label class="text-xs font-medium text-gray-700">Timeline Remarks</label>
          <span class="text-[11px] text-gray-400">Optional</span>
        </div>
        <textarea
          [(ngModel)]="callRemarks"
          rows="3"
          placeholder="Enter remarks for this call (saved to patient timeline)..."
          class="w-full text-xs p-2.5 rounded-lg border border-gray-200 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none resize-none transition"
        ></textarea>
      </div>

      <!-- Action buttons -->
      <div class="flex items-center justify-end gap-2">
        <button
          type="button"
          (click)="saveTimelineAndClose(true)"
          [disabled]="isSavingTimeline"
          class="btn btn-sm btn-secondary text-xs"
        >
          Skip
        </button>
        <button
          type="button"
          (click)="saveTimelineAndClose(false)"
          [disabled]="isSavingTimeline"
          class="btn btn-sm bg-teal-600 hover:bg-teal-700 text-white border-0 gap-1.5 flex items-center px-4"
        >
          <span *ngIf="isSavingTimeline" class="loading loading-spinner loading-xs"></span>
          <app-icon *ngIf="!isSavingTimeline" name="check" [size]="14"></app-icon>
          <span>{{ isSavingTimeline ? 'Saving...' : 'Save & Close' }}</span>
        </button>
      </div>
    </ng-container>

    <!-- ─── IDLE / IN-PROGRESS STATE ─────────────────────────────────── -->
    <ng-container *ngIf="!callCompleted">

      <!-- Call Icon — pulses when ringing or in-progress -->
      <div
        class="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 transition-all duration-300"
        [ngClass]="{
          'bg-teal-50   text-teal-600':   !isCallInProgress,
          'bg-amber-100 text-amber-600 animate-pulse': isCallInProgress && (callStatus === 'ringing' || callStatus === 'Ringing...'),
          'bg-emerald-100 text-emerald-600 animate-pulse': isCallInProgress && callStatus === 'in-progress',
          'bg-red-50    text-red-500':    isCallInProgress && (callStatus === 'busy' || callStatus === 'no-answer' || callStatus === 'failed' || callStatus === 'canceled'),
          'bg-gray-100  text-gray-500':   isCallInProgress && (!callStatus || callStatus === 'Initiating...')
        }"
      >
        <app-icon name="phone" [size]="32"></app-icon>
      </div>

      <!-- ─── Idle: Ready to call ───────────────────────────────────────── -->
      <div *ngIf="!isCallInProgress">
        <h3 class="text-lg font-semibold text-gray-900 mb-1">Call {{ resolvedPatientName }}?</h3>
        <p class="text-gray-500 font-mono text-sm mb-2" *ngIf="resolvedPhoneNumber">
          {{ resolvedPhoneNumber }}
        </p>
        <p class="text-red-500 text-xs mb-2" *ngIf="!resolvedPhoneNumber">
          No phone number found for this patient.
        </p>
        <p class="text-xs text-gray-400 mb-6">
          Initiates a secure call through the Exotel telephony bridge.
        </p>
      </div>

      <!-- ─── In-Progress: Live status + timer ─────────────────────────── -->
      <div *ngIf="isCallInProgress">
        <h3 class="text-lg font-semibold text-gray-900 mb-1">Calling {{ resolvedPatientName }}</h3>
        <p class="text-gray-500 font-mono text-sm mb-3">{{ resolvedPhoneNumber }}</p>

        <!-- Live status badge + timer row -->
        <div class="flex items-center justify-center gap-3 mb-6">
          <div class="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full"
            [ngClass]="{
              'bg-amber-50':   callStatus === 'ringing' || callStatus === 'Ringing...',
              'bg-emerald-50': callStatus === 'in-progress',
              'bg-red-50':     callStatus === 'busy' || callStatus === 'no-answer' || callStatus === 'failed' || callStatus === 'canceled',
              'bg-gray-100':   !callStatus || callStatus === 'Initiating...'
            }"
          >
            <!-- Animated status dot -->
            <span class="w-2 h-2 rounded-full flex-shrink-0"
              [ngClass]="{
                'bg-amber-400 animate-ping':   callStatus === 'ringing' || callStatus === 'Ringing...',
                'bg-emerald-500 animate-ping': callStatus === 'in-progress',
                'bg-red-400':                  callStatus === 'busy' || callStatus === 'no-answer' || callStatus === 'failed' || callStatus === 'canceled',
                'bg-gray-300 animate-pulse':   !callStatus || callStatus === 'Initiating...'
              }"
            ></span>

            <!-- Status label -->
            <span class="font-semibold text-sm"
              [ngClass]="{
                'text-amber-700':   callStatus === 'ringing' || callStatus === 'Ringing...',
                'text-emerald-700': callStatus === 'in-progress',
                'text-red-600':     callStatus === 'busy' || callStatus === 'no-answer' || callStatus === 'failed' || callStatus === 'canceled',
                'text-gray-500':    !callStatus || callStatus === 'Initiating...'
              }"
            >
              <ng-container [ngSwitch]="callStatus">
                <ng-container *ngSwitchCase="'ringing'">Ringing…</ng-container>
                <ng-container *ngSwitchCase="'Ringing...'">Ringing…</ng-container>
                <ng-container *ngSwitchCase="'in-progress'">Connected</ng-container>
                <ng-container *ngSwitchCase="'busy'">Line busy</ng-container>
                <ng-container *ngSwitchCase="'no-answer'">No answer</ng-container>
                <ng-container *ngSwitchCase="'failed'">Call failed</ng-container>
                <ng-container *ngSwitchCase="'canceled'">Cancelled</ng-container>
                <ng-container *ngSwitchDefault>Connecting…</ng-container>
              </ng-container>
            </span>
          </div>

          <!-- Live timer — only shown when actually connected -->
          <div
            *ngIf="callStatus === 'in-progress'"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full"
          >
            <span class="text-gray-700 font-mono font-semibold text-base">{{ formatCallTimer() }}</span>
          </div>
        </div>
      </div>

      <!-- ─── Error Message ──────────────────────────────────────────────── -->
      <div *ngIf="callError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-left">
        <div class="flex items-start gap-2">
          <app-icon name="alert-circle" [size]="16" class="text-red-600 shrink-0 mt-0.5"></app-icon>
          <p class="text-red-700 text-xs leading-relaxed">{{ callError }}</p>
        </div>
      </div>

      <!-- ─── Action Buttons ─────────────────────────────────────────────── -->
      <div class="flex justify-center gap-3">
        <button
          *ngIf="!isCallInProgress"
          type="button"
          (click)="close()"
          class="btn btn-secondary btn-sm"
        >
          Cancel
        </button>

        <button
          *ngIf="!isCallInProgress"
          type="button"
          (click)="initiateCall()"
          class="btn btn-primary btn-sm bg-teal-700 hover:bg-teal-800 border-teal-700 hover:border-teal-800 gap-2 flex items-center text-white"
          [disabled]="!resolvedPhoneNumber"
        >
          <app-icon name="phone" [size]="14"></app-icon>
          <span>Call Now</span>
        </button>

        <!-- End Call button: shown during active call -->
        <button
          *ngIf="isCallInProgress"
          type="button"
          (click)="endCall()"
          class="btn btn-error btn-sm gap-2 flex items-center bg-red-600 hover:bg-red-700 text-white"
        >
          <app-icon name="phone-off" [size]="14"></app-icon>
          <span>End Call</span>
        </button>
      </div>

      <!-- ─── Direct Dial Fallback ────────────────────────────────────────── -->
      <div
        *ngIf="!isCallInProgress && resolvedPhoneNumber"
        class="mt-4 pt-4 border-t border-gray-100"
      >
        <a
          [href]="'tel:' + resolvedPhoneNumber"
          class="text-xs text-gray-500 hover:text-teal-700 inline-flex items-center gap-1.5 transition"
        >
          <app-icon name="phone" [size]="12"></app-icon>
          <span>Or dial directly: {{ resolvedPhoneNumber }}</span>
        </a>
      </div>

    </ng-container>
  </div>
</div>
